import type { CityMotion, CityObjectData, MotionPoint } from "./api/types";

export const defaultMotion = (): CityMotion => ({
  enabled: true,
  mode: "pingpong",
  speed: 2,
  fps: 8,
  orient: false,
  points: [{ x: 0, y: 0, wait: 0, frame: null }],
});

type MotionSample = {
  x: number;
  y: number;
  frame: number | null;
  direction: { x: number; y: number } | null;
  active: boolean;
};

type Leg = {
  from: MotionPoint;
  to: MotionPoint;
  start: number;
  end: number;
  direction: { x: number; y: number } | null;
};

function safeSpeed(value: number | undefined, fallback: number): number {
  const next = Number.isFinite(value) ? Number(value) : fallback;
  return Math.max(0.01, Math.min(100, next));
}

export function sampleMotion(item: CityObjectData, seconds: number): MotionSample {
  const motion = item.motion;
  const base: MotionSample = {
    x: item.x,
    y: item.y,
    frame: null,
    direction: null,
    active: false,
  };
  if (!motion?.enabled || motion.points.length < 2) return base;

  const points = motion.points.slice(0, 256);
  const legs: Leg[] = [];
  let total = 0;

  const add = (
    from: MotionPoint,
    to: MotionPoint,
    duration: number,
    direction: { x: number; y: number } | null,
  ) => {
    if (duration <= 0) return;
    legs.push({ from, to, start: total, end: total + duration, direction });
    total += duration;
  };

  const wait = (point: MotionPoint) => {
    add(point, point, Math.max(0, Math.min(3600, point.wait || 0)), null);
  };

  const travel = (from: MotionPoint, to: MotionPoint) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const distance = Math.hypot(dx, dy);
    add(
      from,
      to,
      distance / safeSpeed(from.speed, safeSpeed(motion.speed, 2)),
      distance ? { x: dx, y: dy } : null,
    );
  };

  wait(points[0]);
  for (let index = 1; index < points.length; index += 1) {
    travel(points[index - 1], points[index]);
    wait(points[index]);
  }
  if (motion.mode === "loop") {
    travel(points[points.length - 1], points[0]);
  } else if (motion.mode === "pingpong") {
    for (let index = points.length - 2; index >= 0; index -= 1) {
      travel(points[index + 1], points[index]);
      if (index > 0) wait(points[index]);
    }
  }

  if (!total || !legs.length) return base;

  let time = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  if (motion.mode === "once" && time >= total) {
    const last = points[points.length - 1];
    return {
      x: item.x + last.x,
      y: item.y + last.y,
      frame: last.frame ?? null,
      direction: null,
      active: true,
    };
  }

  time %= total;
  const leg = legs.find((entry) => time < entry.end) ?? legs[legs.length - 1];
  const span = Math.max(0.0001, leg.end - leg.start);
  const progress = Math.max(0, Math.min(1, (time - leg.start) / span));
  return {
    x: item.x + leg.from.x + (leg.to.x - leg.from.x) * progress,
    y: item.y + leg.from.y + (leg.to.y - leg.from.y) * progress,
    frame: leg.from.frame ?? null,
    direction: leg.direction,
    active: true,
  };
}
